/**
 * Orquesta generación de manifiesto: filtrar por carrier → PDF → actualizar estados
 */
import { useCallback, useState } from 'react'
import toast from 'react-hot-toast'
import { generarPDFManifiesto } from '../../components/pdf-templates/ManifiestoPDF'
import {
  enrichOrdersWithDetail,
  getManifiestoOrdersByStatus,
} from '../../services/manifiestos/manifiestosService'
import { updateOrderStatus } from '../../services/orders/updateOrderStatusService'
import {
  MANIFIESTO_TARGET_STATUS,
  type ManifiestoCarrierConfig,
} from '../../types/manifiestos'
import { filterOrdersByCarrier } from '../../utils/manifiestos/carrierMatch'
import { mapOrdersToManifiestoRows } from '../../utils/manifiestos/manifiestoMapper'

export interface GenerateManifiestoResult {
  success: boolean
  updated: number
  failed: number
  skippedWithoutGuia: number
  fileName?: string
}

export function useGenerateManifiesto(onSuccess?: () => void) {
  const [generatingCarrierId, setGeneratingCarrierId] = useState<string | null>(
    null
  )

  const generate = useCallback(
    async (
      carrier: ManifiestoCarrierConfig,
      cachedOrders?: unknown[]
    ): Promise<GenerateManifiestoResult> => {
      setGeneratingCarrierId(carrier.id)

      try {
        const allOrders =
          cachedOrders && cachedOrders.length > 0
            ? cachedOrders
            : await getManifiestoOrdersByStatus()

        const carrierOrders = filterOrdersByCarrier(allOrders, carrier)
        const detailedOrders = await enrichOrdersWithDetail(carrierOrders)
        const { rows, skippedWithoutGuia } =
          mapOrdersToManifiestoRows(detailedOrders)

        if (rows.length === 0) {
          toast.error(
            skippedWithoutGuia > 0
              ? `No hay guías válidas para ${carrier.label} (${skippedWithoutGuia} sin guía)`
              : `No hay despachos listos para ${carrier.label}`,
            { position: 'top-center' }
          )
          return {
            success: false,
            updated: 0,
            failed: 0,
            skippedWithoutGuia,
          }
        }

        const { fileName } = await generarPDFManifiesto(carrier, rows)

        let updated = 0
        let failed = 0
        const failedIds: number[] = []

        for (const row of rows) {
          if (!row.orderId) {
            failed += 1
            continue
          }
          try {
            await updateOrderStatus(row.orderId, {
              status: MANIFIESTO_TARGET_STATUS,
            })
            updated += 1
          } catch {
            failed += 1
            failedIds.push(row.orderId)
          }
        }

        if (failed === 0) {
          toast.success(
            `Manifiesto generado · ${updated} pedidos actualizados a Manifiesto impreso`,
            { position: 'top-center', duration: 4500 }
          )
        } else if (updated > 0) {
          toast.error(
            `PDF generado, pero ${failed} pedido(s) no se actualizaron${failedIds.length ? `: ${failedIds.slice(0, 5).join(', ')}` : ''}`,
            { position: 'top-center', duration: 6000 }
          )
        } else {
          toast.error(
            'PDF generado, pero no se pudo actualizar ningún pedido',
            { position: 'top-center' }
          )
        }

        if (skippedWithoutGuia > 0) {
          toast(
            `${skippedWithoutGuia} pedido(s) omitidos por no tener guía`,
            { icon: '⚠️', position: 'top-center' }
          )
        }

        onSuccess?.()

        return {
          success: updated > 0 || failed === 0,
          updated,
          failed,
          skippedWithoutGuia,
          fileName,
        }
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : `Error al generar manifiesto de ${carrier.label}`
        toast.error(message, { position: 'top-center' })
        return {
          success: false,
          updated: 0,
          failed: 0,
          skippedWithoutGuia: 0,
        }
      } finally {
        setGeneratingCarrierId(null)
      }
    },
    [onSuccess]
  )

  return {
    generate,
    generatingCarrierId,
  }
}
